/**
 * TaskUserSelect
 *
 * Multi-select user search scoped to OFFICER and ADMIN user types only.
 * Searches across the entire business unit — no company filter needed.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { profileService } from "@/features/profile/services/visaService";
import { Avatar } from "@/shared/components/ui/Avatar";
import { formatDisplayName } from "@/shared/utils/formatters";

export interface TaskUserOption {
  id: string;
  name: string;
  userType: string;
}

interface TaskUserSelectProps {
  selected: TaskUserOption[];
  onChange: (users: TaskUserOption[]) => void;
  label?: string;
  placeholder?: string;
  excludeIds?: string[];
}

export const TaskUserSelect: React.FC<TaskUserSelectProps> = ({
  selected,
  onChange,
  label,
  placeholder,
  excludeIds = [],
}) => {
  const { t } = useTranslation();
  const [query, setQuery]     = useState("");
  const [results, setResults] = useState<TaskUserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const wrapperRef            = useRef<HTMLDivElement>(null);
  const inputRef              = useRef<HTMLInputElement>(null);
  const debounceRef           = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const doSearch = useCallback(
    (q: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const params: Record<string, string> = { user_type: "OFFICER,ADMIN" };
          if (q.trim()) params.name = q.trim();
          const res = await profileService.searchUsers(params);
          const list: any[] = Array.isArray(res) ? res : res.users ?? [];
          const blocked = new Set([...selected.map((s) => s.id), ...excludeIds]);
          setResults(
            list
              .filter((u) => !blocked.has(u.user_id))
              .slice(0, 12)
              .map((u) => ({
                id:       u.user_id,
                name:     formatDisplayName(u.last_name, u.first_name, u.middle_name ?? ""),
                userType: (u.user_type ?? "officer").toLowerCase(),
              })),
          );
          setOpen(true);
        } catch {
          setResults([]);
        } finally {
          setLoading(false);
        }
      }, 150);
    },
    [selected, excludeIds],
  );

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    doSearch(e.target.value);
  };

  const handleSelect = (user: TaskUserOption) => {
    onChange([...selected, user]);
    setQuery("");
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleRemove = (id: string) => {
    onChange(selected.filter((s) => s.id !== id));
  };

  return (
    <div ref={wrapperRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map((u) => (
            <span
              key={u.id}
              className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
            >
              <Avatar firstName={u.name} size="sm" />
              <span className="max-w-[160px] truncate">{u.name}</span>
              <button
                type="button"
                onClick={() => handleRemove(u.id)}
                className="ml-0.5 hover:text-indigo-900 transition-colors"
                aria-label={`Remove ${u.name}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="w-4 h-4 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => { if (results.length > 0) { setOpen(true); } else { doSearch(query); } }}
          placeholder={placeholder ?? t("tasks.form.field_assignees_placeholder", "Search officers...")}
          className="w-full pl-9 pr-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        {loading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <div className="w-4 h-4 border-2 border-indigo-300 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {results.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => handleSelect(u)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-indigo-50 transition-colors"
            >
              <Avatar firstName={u.name} size="sm" />
              <span className="flex-1 truncate text-gray-800">{u.name}</span>
            </button>
          ))}
        </div>
      )}

      {open && !loading && query.trim() && results.length === 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-4 text-sm text-gray-400 text-center">
          {t("inquiry.no_results")}
        </div>
      )}
    </div>
  );
};
